import PropTypes from "prop-types";
import * as React from "react";
import {
  addCharacter,
  characterList,
  removeCharacter,
} from "../../../domain/productComposer/characters";
import { useCharacters, useDebounce } from "../../../hooks";
import { Autocomplete, Chip, Typography } from "../../../themeV2";
import css from "./sections.module.css";

const MIN_QUERY = 2;

const CharacterPicker = function CharacterPicker({ characters, onChange }) {
  const [inputValue, setInputValue] = React.useState("");
  const [query, setQuery] = React.useState("");
  useDebounce(() => setQuery(inputValue.trim()), 300, [inputValue]);

  const { characters: results } = useCharacters({
    q: query.length >= MIN_QUERY ? query : null,
  });
  const assigned = characterList(characters);
  const assignedIds = new Set(assigned.map((c) => c.franchiseCharacterId));
  const available = results.filter(
    (c) => !assignedIds.has(c.franchise_character_id),
  );

  return (
    <div className={css.fields}>
      <Typography variant="title-sm" as="h4">
        Assigned characters
      </Typography>

      {assigned.length > 0 && (
        <div className={css.chips}>
          {assigned.map((c) => (
            <Chip
              key={`${c.franchiseId}-${c.characterId}`}
              variant="input"
              size="sm"
              label={`${c.characterName} (${c.franchiseName})`}
              onDelete={() =>
                onChange(
                  removeCharacter(characters, {
                    franchiseId: c.franchiseId,
                    characterId: c.characterId,
                  }),
                )
              }
            />
          ))}
        </div>
      )}

      <Autocomplete.Root
        label="Search characters to assign"
        placeholder="Type a character name"
        value={null}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        onValueChange={(character) => {
          if (!character) return;
          onChange(addCharacter(characters, character));
          setInputValue("");
        }}
        /* The results arrive already matched by the server. */
        autoComplete="none"
        itemToStringLabel={(c) => c?.name ?? ""}
      >
        {available.map((character) => (
          <Autocomplete.Item
            key={character.franchise_character_id}
            value={character}
          >
            {`${character.name} (${character.franchise_name})`}
          </Autocomplete.Item>
        ))}
        <Autocomplete.Empty>
          {query.length >= MIN_QUERY
            ? "No matching characters found."
            : "Type at least two letters to search."}
        </Autocomplete.Empty>
      </Autocomplete.Root>
    </div>
  );
};

CharacterPicker.propTypes = {
  characters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CharacterPicker;
